/**
 * Draft encryption (roadmap 3.3): AES-256-GCM, a fresh 96-bit IV for every write, and associated data
 * that binds a ciphertext to its person, form, entity and schema version, so a draft can never be
 * replayed into another person's form, another form, another record or an incompatible version of the
 * form. The key is imported non-extractable: script can use it but never read it out.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

export function draftAad(personId: string, formKey: string, entityId: string, schemaVersion: number): Uint8Array<ArrayBuffer> {
  return enc.encode(`mdt-draft/1|${personId}|${formKey}|${entityId}|${schemaVersion}`) as Uint8Array<ArrayBuffer>;
}

function fromB64url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** The server's 32-byte key material (GET /api/session/draft-key) as a non-extractable AES-GCM key. */
export async function importDraftKey(keyB64url: string): Promise<CryptoKey> {
  const raw = fromB64url(keyB64url);
  if (raw.length !== 32) throw new Error('draft key must be 32 bytes');
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  raw.fill(0);
  return key;
}

export async function sealDraft(key: CryptoKey, aad: Uint8Array<ArrayBuffer>, value: unknown): Promise<{ iv: Uint8Array<ArrayBuffer>; ciphertext: ArrayBuffer }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, enc.encode(JSON.stringify(value)));
  return { iv, ciphertext };
}

/** Throws if the key, the associated data or a single bit of the ciphertext is wrong. */
export async function openDraft(key: CryptoKey, aad: Uint8Array<ArrayBuffer>, iv: Uint8Array, ciphertext: ArrayBuffer): Promise<unknown> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv), additionalData: aad }, key, ciphertext);
  return JSON.parse(dec.decode(plain)) as unknown;
}
